import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  Avatar,
  FormControl,
  InputLabel,
  Button,
  Chip,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_TASKS } from "../../../apis/task-apis/taskApis";
import { exportTasksToExcel } from "../utils/exportToExcel";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import { DomainType, DomainTypeLabel } from "../../../utils/enums";
import AccessControl, { FEATURE_IDS } from "../../../components/AccessControl";

const TaskSidebar = ({
  selectedDomain,
  setSelectedDomain,
  selectedOwner,
  setSelectedOwner,
}) => {
  const getInitials = (name, email) => {
    if (name) {
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    if (email) return email[0].toUpperCase();
    return "?";
  };

  const { data } = useQuery(GET_TASKS, {
    variables: {
      filter: {},
      page: 1,
      limit: 1000,
    },
  });

  const tasks = data?.getTasks?.data || [];
  const ownerOptions = useMemo(() => {
    const byId = new Map();
    tasks.forEach((task) => {
      const owner = task?.task_owner_data;
      if (owner?.id) {
        byId.set(owner.id, owner.name || owner.email || "Unknown");
      }
    });
    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks]);

  const domainCounts = tasks.reduce((acc, task) => {
    if (task.domains && task.domains.length > 0) {
      task.domains.forEach((domain) => {
        acc[domain.key] = (acc[domain.key] || 0) + 1;
      });
    }
    return acc;
  }, {});

  const domains = [
    {
      key: DomainType.INFRA,
      label: DomainTypeLabel[DomainType.INFRA],
      count: domainCounts.infra || 0,
    },
    {
      key: DomainType.RISK,
      label: DomainTypeLabel[DomainType.RISK],
      count: domainCounts.risk || 0,
    },
    {
      key: DomainType.PEOPLE,
      label: DomainTypeLabel[DomainType.PEOPLE],
      count: domainCounts.people || 0,
    },
  ];

  const handleClearFilters = () => {
    setSelectedDomain(null);
    setSelectedOwner("__all__");
  };
  const handleExport = () => {
    exportTasksToExcel(tasks);
  };

  const dropdownSx = {
    borderRadius: "8px",
    backgroundColor: "white",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      gap: 1,
      py: 0.9,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        mt: 0.75,
        borderRadius: "10px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        border: "1px solid #E5E7EB",
        "& .MuiMenuItem-root": {
          fontSize: "0.875rem",
          py: 1,
          px: 1.5,
        },
      },
    },
  };

  const ownerById = ownerOptions.find((o) => o.id === selectedOwner);
  const activeFiltersCount = Number(Boolean(selectedDomain)) + Number(selectedOwner !== "__all__");

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        width: "100%",
        gap: 1.5,
        flexWrap: { xs: "wrap", md: "nowrap" },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        flexWrap="wrap"
        sx={{
          p: 1,
          pr: 1.25,
          borderRadius: "10px",
          border: "1px solid #E5E7EB",
          backgroundColor: "#F9FAFB",
          flex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 0.25 }}>
          <FilterListIcon sx={{ color: "#6B7280", fontSize: 18 }} />
          <Typography variant="body2" color="#374151" fontWeight={600} fontSize="0.875rem">
            Filters
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              size="small"
              label={activeFiltersCount}
              sx={{
                ml: 0.25,
                height: 20,
                minWidth: 20,
                backgroundColor: "#DBEAFE",
                color: "#1D4ED8",
                fontWeight: 600,
                fontSize: "0.72rem",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
        </Box>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Domain</InputLabel>
          <Select
            value={selectedDomain ?? "__all__"}
            onChange={(e) =>
              setSelectedDomain(e.target.value === "__all__" ? null : e.target.value)
            }
            label="Domain"
            sx={dropdownSx}
            MenuProps={menuProps}
            renderValue={(value) => {
              if (value === "__all__")
                return <Typography fontSize="0.875rem" color="#6B7280">All domains</Typography>;
              const d = domains.find((x) => x.key === value);
              return (
                <Typography fontSize="0.875rem" color="#111827" fontWeight={500}>
                  {d?.label}
                </Typography>
              );
            }}
          >
            <MenuItem value="__all__">All domains</MenuItem>
            {domains.map((domain) => (
              <MenuItem key={domain.key} value={domain.key}>
                <Typography fontSize="0.875rem">{domain.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 210 }}>
          <InputLabel>Owner</InputLabel>
          <Select
            value={selectedOwner}
            onChange={(e) => setSelectedOwner(e.target.value)}
            label="Owner"
            sx={dropdownSx}
            MenuProps={menuProps}
            renderValue={(value) => {
              if (value === "__all__")
                return <Typography fontSize="0.875rem" color="#6B7280">All owners</Typography>;
              if (value === "__unassigned__")
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar sx={{ width: 22, height: 22, backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
                      <PersonOffOutlinedIcon sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Typography fontSize="0.875rem" color="#111827" fontWeight={500}>Unassigned</Typography>
                  </Box>
                );
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      backgroundColor: "#1976d2",
                      color: "#fff",
                    }}
                  >
                    {getInitials(ownerById?.label, "")}
                  </Avatar>
                  <Typography fontSize="0.875rem" color="#111827" fontWeight={500} noWrap>
                    {ownerById?.label}
                  </Typography>
                </Box>
              );
            }}
          >
            <MenuItem value="__all__">
              <Typography fontSize="0.875rem">All owners</Typography>
            </MenuItem>
            <MenuItem value="__unassigned__">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Avatar sx={{ width: 28, height: 28, backgroundColor: "#EEF2FF", color: "#4F46E5" }}>
                  <PersonOffOutlinedIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Box>
                  <Typography fontSize="0.875rem" fontWeight={500}>Unassigned</Typography>
                  <Typography fontSize="0.72rem" color="text.secondary">No owner</Typography>
                </Box>
              </Box>
            </MenuItem>
            {ownerOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      backgroundColor: "#1976d2",
                      color: "#fff",
                    }}
                  >
                    {getInitials(o.label, "")}
                  </Avatar>
                  <Typography fontSize="0.875rem" fontWeight={500}>{o.label}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {(selectedDomain || selectedOwner !== "__all__") && (
          <Button
            size="small"
            onClick={handleClearFilters}
            startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
            sx={{
              ml: { xs: 0, sm: 0.5 },
              textTransform: "none",
              color: "#3B82F6",
              fontWeight: 500,
              fontSize: "0.8rem",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "rgba(59,130,246,0.08)" },
            }}
          >
            Clear filters
          </Button>
        )}
      </Stack>

      <AccessControl featureId={FEATURE_IDS.TASKS_EXCEL_EXPORT}>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={handleExport}
          sx={{
            height: 36,
            px: 1.75,
            borderRadius: "8px",
            textTransform: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            boxShadow: "none",
            alignSelf: { xs: "flex-start", md: "center" },
            "&:hover": {
              boxShadow: "none",
            },
          }}
        >
          Export Tasks
        </Button>
      </AccessControl>
    </Stack>
  );
};

export default TaskSidebar;
